import { alpha, hex, rgba, shadow, type ColorValue, type TokenValue } from './value.ts';

/**
 * The theme-dependent half of §9: "dark mode default; both themes are token swaps".
 *
 * **Colour is a vocabulary here, not decoration.** The app's whole character is that it
 * distinguishes states a video frontend usually blurs together — "no data" from "we dropped it",
 * "the stream is live" from "something failed", "we substituted the sort order" from "this is what
 * you asked for" — so each hue is spoken for and used for nothing else:
 *
 * - `accent` — anything you can act on: links, the active nav item, a selected menu row, focus.
 * - `live` — *media truth only*: the played part of the bar and the LIVE badge. Never an error.
 *   A playhead is not a warning, and sharing red between the two makes every video look broken.
 * - `caution` — a caveat about what is on screen: a rescued comment list, a dropped item, a
 *   partial result. The reader is not being told off; they are being told the truth.
 * - `positive` — a confirmation: something the reader asked for happened.
 * - `negative` — a failure the reader can act on.
 *
 * Declaring every role explicitly is what makes "a theme is a token swap" a **compile error** when one
 * is missed, rather than a visual bug where the dark value shows through a light page because
 * `[data-theme='light']` happened not to override it. It is a `type` rather than an `interface` for a
 * TypeScript reason `flatten.ts` explains: only a type alias is walkable as a record.
 */
export type ColorRoles = {
  /** The page. */
  readonly bg: ColorValue;
  /** A panel on the page: the description box, a card. */
  readonly surface: ColorValue;
  /** A well *inside* a surface: inputs, hovered rows, the thumbnail box before its image lands. */
  readonly surfaceAlt: ColorValue;
  /** Floating above everything: menus and popovers. Pairs with `shadow.popover`. */
  readonly surfaceRaised: ColorValue;
  readonly border: ColorValue;
  /**
   * Where a border is the only thing describing a control's edge — a text field, an outlined button.
   * Held to 3:1 against `bg` and `surface` (WCAG SC 1.4.11), which is why it is a visibly heavier
   * grey than `border`: that one separates sections and is decorative, this one *is* the control.
   */
  readonly borderStrong: ColorValue;
  readonly text: ColorValue;
  /** Secondary prose: a byline, a lede, a caption. */
  readonly textMuted: ColorValue;
  /** Tertiary: a timestamp, a hint, a slice number. Still ≥4.5:1 — see `contrast.ts`. */
  readonly textFaint: ColorValue;
  readonly accent: ColorValue;
  /** A low-emphasis accent wash: the selected chip, the current chapter's row. */
  readonly accentQuiet: ColorValue;
  /** Text and icons on top of an `accent` fill. */
  readonly onAccent: ColorValue;
  readonly focusRing: ColorValue;
  /** Media truth: the played bar, the LIVE badge. */
  readonly live: ColorValue;
  /** Text and icons over video, where the backdrop is arbitrary. */
  readonly onMedia: ColorValue;
  /**
   * The three washes that sit **on video** — a control's hover, the unplayed part of a bar, the
   * buffered part. They are white in *both* themes, and that is the point: video is not a theme.
   *
   * Discovered by looking at the light theme in a browser. The generic `hover`/`selected` washes flip
   * to black alpha there, which is correct on a page and invisible on a dark frame — the chaptered
   * scrub bar simply vanished while every token in the palette was still "right".
   */
  readonly mediaHover: ColorValue;
  readonly mediaTrack: ColorValue;
  readonly mediaBuffered: ColorValue;
  /** The gradient behind the player chrome — white subtitles on a white frame is the failure it fixes. */
  readonly scrim: ColorValue;
  /** Hover and pressed washes. Translucent so they work over a surface *or* over video. */
  readonly hover: ColorValue;
  readonly selected: ColorValue;
  readonly caution: ColorValue;
  readonly positive: ColorValue;
  readonly negative: ColorValue;
};

export interface Theme {
  readonly name: string;
  /**
   * The CSS `color-scheme` for this theme. It is a token in its own right because it decides what
   * the *browser* paints — scrollbars, form controls, the canvas behind an overscrolled page — and
   * forgetting it is how a light theme ends up with black scrollbars.
   */
  readonly scheme: 'dark' | 'light';
  readonly color: ColorRoles;
  readonly shadow: {
    /** A resting card. */
    readonly raised: TokenValue;
    /** A menu that has left the page surface. */
    readonly popover: TokenValue;
  };
}

const DARK_ACCENT = hex('#78adff');
const LIGHT_ACCENT = hex('#1a58c4');
const LIGHT_INK = hex('#101520');

/**
 * Graphite rather than neutral grey: every step carries a little blue, so the canvas reads as cool
 * and the one warm hue in the system (`caution`) cannot be mistaken for "a slightly different grey".
 */
export const darkTheme: Theme = {
  name: 'dark',
  scheme: 'dark',
  color: {
    bg: hex('#0b0d10'),
    surface: hex('#12161b'),
    surfaceAlt: hex('#191e25'),
    surfaceRaised: hex('#1c222a'),
    border: hex('#242b34'),
    borderStrong: hex('#5c6674'),
    text: hex('#e8ebef'),
    textMuted: hex('#a3adba'),
    textFaint: hex('#8b95a3'),
    accent: DARK_ACCENT,
    accentQuiet: alpha(DARK_ACCENT, 0.16),
    onAccent: hex('#06111f'),
    focusRing: hex('#9cc4ff'),
    live: hex('#ff5a5f'),
    onMedia: hex('#f6f8fb'),
    mediaHover: rgba(255, 255, 255, 0.18),
    mediaTrack: rgba(255, 255, 255, 0.28),
    mediaBuffered: rgba(255, 255, 255, 0.45),
    scrim: rgba(0, 0, 0, 0.82),
    hover: rgba(255, 255, 255, 0.08),
    selected: rgba(255, 255, 255, 0.14),
    caution: hex('#f3b65c'),
    positive: hex('#5cd9a2'),
    negative: hex('#ff9ba3'),
  },
  shadow: {
    raised: shadow({ xPx: 0, yPx: 2, blurPx: 8, spreadPx: 0, color: rgba(0, 0, 0, 0.4) }),
    popover: shadow({ xPx: 0, yPx: 14, blurPx: 34, spreadPx: 0, color: rgba(0, 0, 0, 0.55) }),
  },
};

/**
 * Not white: `bg` is paper and `surface` is white, so a panel *rises* out of the page instead of
 * being outlined on it. The same shape as the dark theme, where `surface` is lighter than `bg`.
 *
 * `onMedia` and `scrim` do not flip. They sit on video, and video is not a theme.
 */
export const lightTheme: Theme = {
  name: 'light',
  scheme: 'light',
  color: {
    bg: hex('#f5f7f9'),
    surface: hex('#ffffff'),
    surfaceAlt: hex('#eceff3'),
    surfaceRaised: hex('#ffffff'),
    border: hex('#dbe0e7'),
    borderStrong: hex('#848c9a'),
    text: LIGHT_INK,
    textMuted: hex('#525a68'),
    textFaint: hex('#5c6472'),
    accent: LIGHT_ACCENT,
    accentQuiet: alpha(LIGHT_ACCENT, 0.12),
    onAccent: hex('#ffffff'),
    focusRing: LIGHT_ACCENT,
    live: hex('#c92a2f'),
    onMedia: hex('#f6f8fb'),
    mediaHover: rgba(255, 255, 255, 0.18),
    mediaTrack: rgba(255, 255, 255, 0.28),
    mediaBuffered: rgba(255, 255, 255, 0.45),
    scrim: rgba(0, 0, 0, 0.82),
    hover: alpha(LIGHT_INK, 0.06),
    selected: alpha(LIGHT_INK, 0.1),
    caution: hex('#8a5a00'),
    positive: hex('#0f6b4c'),
    negative: hex('#b32229'),
  },
  shadow: {
    raised: shadow({ xPx: 0, yPx: 1, blurPx: 3, spreadPx: 0, color: alpha(LIGHT_INK, 0.1) }),
    popover: shadow({ xPx: 0, yPx: 14, blurPx: 34, spreadPx: -6, color: alpha(LIGHT_INK, 0.22) }),
  },
};

/** Dark first: it is the default (§9), and the emitter writes the first theme into `:root`. */
export const themes: readonly Theme[] = [darkTheme, lightTheme];
